/**
 * src/lib/voice/useVoiceAutoMode.js — Phase 76-02 Task 2
 *
 * Auto-VAD effects extracted from useVoiceConversation. Owns two
 * short-lived MediaStream + VAD pairs:
 *
 *   1. Idle-listen: when enabled && state === 'idle', a VAD mounts and
 *      fires onSpeechStart() when the user begins speaking → parent
 *      calls startTurn().
 *
 *   2. Silence-end: when enabled && state === 'recording', a second VAD
 *      mounts with onSpeechEnd() and a longer resetMs so it doesn't cut
 *      the user off mid-thought → parent calls stopRecording().
 *
 * No internal state. Returns nothing. Caller composes this under the
 * parent hook, passing state from useVoiceStateMachine.
 *
 * Test seams:
 *   - createVAD (injectable — defaults to the shared factory)
 *   - navigator (injectable — defaults to global navigator)
 */

import { useEffect, useRef } from 'react';
import { createVAD as defaultCreateVAD } from './voiceActivityDetector';
import { logVoiceEvent } from './voiceLogger';
import { registerMic, releaseMic } from './micRegistry';

export default function useVoiceAutoMode({
  enabled,
  state,
  onSpeechStart,
  onSpeechEnd,
  // TICKET-VOICE-CONVO-UVTP — parent receives (err, phase) when getUserMedia
  // rejects so it can setError('Microphone access required — tap to retry').
  // Optional; absence is a no-op so existing tests don't need to mock it.
  onInitError,
  createVAD = defaultCreateVAD,
  navigator: navOverride,
  idleResetMs,
  recordingResetMs = 1200,
} = {}) {
  const onInitErrorRef = useRef(onInitError);
  useEffect(() => { onInitErrorRef.current = onInitError; }, [onInitError]);
  // 2026-04-20 (session 4) — ROOT CAUSE of stuck "Listening...":
  // Previously the effect had `onSpeechEnd` (etc.) in its deps array.
  // Parent useVoiceConversation rebuilds stopRecording (= onSpeechEnd)
  // on every state change, so the effect re-ran on every render of the
  // parent during recording. Each re-run destroyed and recreated the
  // VAD instance — the internal speech-timer reset every time, so VAD
  // never observed sustained silence long enough to fire onSpeechEnd.
  // The 30s maxSpeechMs cap also never hit because the instance rarely
  // lived 30s.
  //
  // Fix: wrap callbacks in always-latest refs. The useEffect only
  // depends on `enabled`, `state`, and resetMs — all stable within a
  // phase. VAD instance lives for the full recording duration.
  const onSpeechStartRef = useRef(onSpeechStart);
  const onSpeechEndRef = useRef(onSpeechEnd);
  useEffect(() => { onSpeechStartRef.current = onSpeechStart; }, [onSpeechStart]);
  useEffect(() => { onSpeechEndRef.current = onSpeechEnd; }, [onSpeechEnd]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (state !== 'idle') return undefined;
    const nav = navOverride || (typeof navigator !== 'undefined' ? navigator : null);
    if (!nav?.mediaDevices?.getUserMedia) return undefined;

    let cancelled = false;
    let localStream = null;
    let localVad = null;
    const instanceStart = Date.now();

    (async () => {
      try {
        const stream = await nav.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
          },
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        localStream = stream;
        registerMic(stream, null, 'useVoiceAutoMode:idle_vad');
        localVad = createVAD({
          stream,
          onSpeechStart: () => {
            logVoiceEvent('auto_vad_start', 'ok', 'Auto-VAD detected speech — starting turn');
            try { onSpeechStartRef.current?.(); } catch (err) {
              console.warn('[useVoiceAutoMode] onSpeechStart failed:', err?.message);
            }
          },
          ...(idleResetMs ? { resetMs: idleResetMs } : {}),
        });
        logVoiceEvent('vad_instance', 'ok', 'idle-VAD mounted', { phase: 'idle' });
      } catch (err) {
        // TICKET-VOICE-CONVO-UVTP (CARL development-005, 2026-05-07) — was a
        // silent console.warn. iOS Safari mic-permission denial / Bluetooth
        // route change at this exact moment killed the idle-VAD invisibly,
        // leaving CEO with an idle orb forever and zero `app_error_logs`
        // signal to debug from. Emit to the spine + surface to user.
        logVoiceEvent('vad_init', 'error', err?.message || 'init_failed', {
          phase: 'idle',
          error_code: err?.name || null,
        });
        try { onInitErrorRef.current?.(err, 'idle'); } catch { /* spine-emit-ok: parent error-callback safety */ }
      }
    })();

    return () => {
      cancelled = true;
      const lifetimeMs = Date.now() - instanceStart;
      if (localVad) { try { localVad.stop(); } catch { /* noop */ } }
      if (localStream) {
        try { releaseMic(localStream); } catch { /* noop */ }
      }
      logVoiceEvent('vad_instance_end', 'ok', `idle-VAD unmounted (${lifetimeMs}ms)`, {
        phase: 'idle',
        lifetime_ms: lifetimeMs,
      });
    };
    // createVAD + navOverride are DI seams — intentionally excluded so
    // a consumer passing an inline factory (tests only) doesn't remount
    // the effect every render. idleResetMs is a stable config number.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, state]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (state !== 'recording') return undefined;
    const nav = navOverride || (typeof navigator !== 'undefined' ? navigator : null);
    if (!nav?.mediaDevices?.getUserMedia) return undefined;

    let cancelled = false;
    let localStream = null;
    let localVad = null;
    const instanceStart = Date.now();

    (async () => {
      try {
        const stream = await nav.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
          },
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        localStream = stream;
        registerMic(stream, null, 'useVoiceAutoMode:recording_vad');
        localVad = createVAD({
          stream,
          onSpeechStart: () => { /* noop — already recording */ },
          onSpeechEnd: () => {
            logVoiceEvent('auto_vad_end', 'ok', 'Auto-VAD detected silence — ending turn');
            try { onSpeechEndRef.current?.(); } catch (err) {
              console.warn('[useVoiceAutoMode] onSpeechEnd failed:', err?.message);
            }
          },
          resetMs: recordingResetMs,
        });
        logVoiceEvent('vad_instance', 'ok', 'recording-VAD mounted', { phase: 'recording' });
      } catch (err) {
        // TICKET-VOICE-CONVO-UVTP — same fix as the idle-VAD branch above.
        // Spine emit + parent callback surface so a mid-recording mic loss
        // (Bluetooth disconnect, system audio interrupt) is visible.
        logVoiceEvent('vad_init', 'error', err?.message || 'init_failed', {
          phase: 'recording',
          error_code: err?.name || null,
        });
        try { onInitErrorRef.current?.(err, 'recording'); } catch { /* spine-emit-ok: parent error-callback safety */ }
      }
    })();

    return () => {
      cancelled = true;
      const lifetimeMs = Date.now() - instanceStart;
      if (localVad) { try { localVad.stop(); } catch { /* noop */ } }
      if (localStream) {
        try { releaseMic(localStream); } catch { /* noop */ }
      }
      logVoiceEvent('vad_instance_end', 'ok', `recording-VAD unmounted (${lifetimeMs}ms)`, {
        phase: 'recording',
        lifetime_ms: lifetimeMs,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, state]);
}
